import { Text } from '../../primitives/Text.tsx';
import { VisuallyHidden } from '../../primitives/VisuallyHidden.tsx';
import styles from './Chart.module.css';

/**
 * Design system § 14 — Chart, column form.
 *
 * One column per period, growing from a single hairline baseline. The marks
 * carry `chart/series-1` and nothing else; every word on the chart takes a
 * § 3.2 text role, because text never wears the series colour.
 *
 * TWO RULES THIS COMPONENT EXISTS TO KEEP:
 *
 *   1. **It plots what it was given.** There is no interpolation, no padding
 *      of a short series, and no "sample" mode. A period with no records is a
 *      zero, drawn as a zero — an administrator reading a flat month needs to
 *      know it was flat, not to be shown a shape.
 *   2. **It carries its own numbers.** The columns are `aria-hidden` and a
 *      table of the same values sits beside them, so nothing the chart says
 *      is available only to a reader who can see it (§ 12).
 *
 * Labels are SELECTIVE: the tallest column and the most recent one wear their
 * value, and the rest give theirs up on hover and in the table. A number over
 * every column is the thing that stops direct labels working.
 */

export interface ColumnPoint {
  /** The axis label — short, because it is drawn under a 24px column. */
  label: string;
  value: number;
  /** The period spelled out, for the table and the tooltip. */
  caption: string;
}

interface ColumnChartProps {
  title: string;
  /** One line under the title: what is counted, and over what period. */
  description?: string;
  points: readonly ColumnPoint[];
  /** The column header of the value column in the table view. */
  valueLabel: string;
  /** Shown in place of the plot when there is nothing recorded at all. */
  emptyMessage: string;
}

export function ColumnChart({
  title,
  description,
  points,
  valueLabel,
  emptyMessage,
}: ColumnChartProps) {
  const max = points.reduce((highest, point) => Math.max(highest, point.value), 0);
  const peak = points.findIndex((point) => point.value === max && max > 0);
  const last = points.length - 1;
  const nothing = points.length === 0 || max === 0;

  return (
    <figure className={styles.figure}>
      <figcaption className={styles.caption}>
        <Text as="h3" variant="subheading">
          {title}
        </Text>
        {description ? (
          <Text as="p" variant="body-sm" tone="muted">
            {description}
          </Text>
        ) : null}
      </figcaption>

      {nothing ? (
        <Text as="p" variant="body-sm" tone="muted">
          {emptyMessage}
        </Text>
      ) : (
        <div className={styles.plot} aria-hidden="true">
          {points.map((point, index) => (
            <div key={point.label} className={styles.slot}>
              <div className={styles.column}>
                {/*
                  The tooltip § 14 asks for: every column gives up its exact
                  value on hover, which is what lets the labels below stay
                  selective.
                */}
                <span className={styles.tip}>
                  {point.caption}: {point.value}
                </span>

                {index === peak || index === last ? (
                  <span className={`${styles.value} type-numeric-inline`}>{point.value}</span>
                ) : null}

                {/*
                  The one inline style in the library, and it is the only
                  place one belongs: a column's height IS the datum. Putting
                  it in the stylesheet would mean a class per value.
                */}
                <span
                  className={styles.mark}
                  data-zero={point.value === 0 || undefined}
                  style={{ height: `${max === 0 ? 0 : (point.value / max) * 100}%` }}
                />
              </div>
              <span className={`${styles.tick} type-caption`}>{point.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* § 14 — the chart's own numbers, for a reader who cannot see the
          marks. The same values, in the same order. */}
      <VisuallyHidden as="div">
        <table>
          <caption>{title}</caption>
          <thead>
            <tr>
              <th scope="col">Período</th>
              <th scope="col">{valueLabel}</th>
            </tr>
          </thead>
          <tbody>
            {points.map((point) => (
              <tr key={point.label}>
                <th scope="row">{point.caption}</th>
                <td>{point.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </VisuallyHidden>
    </figure>
  );
}
